import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarDays, CheckCircle2, Clock3, RefreshCw, Save, Sparkles, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cleanupRiderLineupNos, cleanupRiderNumbers, cleanupRiderReserveFlags, getSeedStatus, runSeasonSeed } from "@/api/adminRiders";
import { clearOfficialMatchLineup, resetOfficialMatchLineupState, updateOfficialMatchLineup } from "@/api/matches";
import { getTeamRiders } from "@/api/teams";
import { useOfficialMatch, useOfficialMatches } from "@/queries/matches";
import { formatSvDateTime, getSvYear, toDate } from "@/utils/date";

const DEFAULT_SEASON = 2026;
const LINEUP_SLOTS = [1, 2, 3, 4, 5, 6, 7];

function slotLabel(slot) {
  return slot >= 6 ? `#${slot} (reserv)` : `#${slot}`;
}

function matchDate(match) {
  return toDate(match?.date || match?.start || match?.start_time);
}

function matchTitle(match) {
  const home = match?.home_team_display || match?.home_team_name_canonical || match?.home_team || "Hemmalag";
  const away = match?.away_team_display || match?.away_team_name_canonical || match?.away_team || "Bortalag";
  return `${home} vs ${away}`;
}

function matchMeta(match) {
  const when = formatSvDateTime(match?.date, { dateStyle: "medium", timeStyle: "short" });
  const venue = match?.venue || match?.location || "";
  return [when, venue].filter(Boolean).join(" • ");
}

function hasLineup(match) {
  return Boolean(match?.lineups?.home?.length && match?.lineups?.away?.length);
}

function matchSeasonValue(match) {
  const rawSeason = match?.season;
  if (rawSeason !== null && rawSeason !== undefined && String(rawSeason).trim() !== "") {
    const explicitSeason = Number(rawSeason);
    if (Number.isFinite(explicitSeason) && explicitSeason >= 2000) return explicitSeason;
  }
  return getSvYear(match?.date || match?.start || match?.start_time);
}

function isUpcoming(match) {
  const dt = matchDate(match);
  return Boolean(dt && !Number.isNaN(dt.getTime()) && dt.getTime() >= Date.now());
}

function matchStatusValue(match) {
  return String(match?.status || "").trim().toLowerCase();
}

function matchSortBucket(match) {
  if (matchStatusValue(match) === "live") return 0;
  if (isUpcoming(match)) return 1;
  return 2;
}

function emptyState() {
  return Object.fromEntries(LINEUP_SLOTS.map((slot) => [String(slot), ""]));
}

function buildLineupState(entries = []) {
  const base = emptyState();
  for (const item of Array.isArray(entries) ? entries : []) {
    const slot = String(item?.lineup_no ?? "");
    if (slot in base) base[slot] = String(item?.rider_id ?? item?.id ?? "");
  }
  return base;
}

function toOptions(payload) {
  const riders = Array.isArray(payload)
    ? payload
    : [...(Array.isArray(payload?.mains) ? payload.mains : []), ...(Array.isArray(payload?.reserves) ? payload.reserves : [])];
  const deduped = new Map();
  for (const rider of riders) {
    const value = String(rider?.id ?? rider?._id ?? "");
    if (!value) continue;
    const label = rider?.name || [rider?.first_name, rider?.last_name].filter(Boolean).join(" ") || "Okänd förare";
    deduped.set(value, { value, label });
  }
  return Array.from(deduped.values()).sort((a, b) => a.label.localeCompare(b.label, "sv-SE"));
}

function lineupToPayload(state, options) {
  const optionMap = new Map(options.map((item) => [item.value, item]));
  return LINEUP_SLOTS.map((slot) => {
    const riderId = String(state[String(slot)] || "");
    const rider = optionMap.get(riderId);
    if (!riderId || !rider) return null;
    return { rider_id: riderId, rider_name: rider.label, lineup_no: slot, is_reserve: slot >= 6 };
  }).filter(Boolean);
}

function sameSelection(a, b) {
  return LINEUP_SLOTS.every((slot) => String(a[String(slot)] || "") === String(b[String(slot)] || ""));
}

function seasons() {
  return [DEFAULT_SEASON + 1, DEFAULT_SEASON, DEFAULT_SEASON - 1, DEFAULT_SEASON - 2, DEFAULT_SEASON - 3].map(String);
}

export default function AdminLineupsPage() {
  const queryClient = useQueryClient();

  const [matchSeason, setMatchSeason] = useState(String(DEFAULT_SEASON));
  const [riderSeason, setRiderSeason] = useState(String(DEFAULT_SEASON));
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("");

  const [homeOptions, setHomeOptions] = useState([]);
  const [awayOptions, setAwayOptions] = useState([]);
  const [homeState, setHomeState] = useState(emptyState);
  const [awayState, setAwayState] = useState(emptyState);

  const [seedSeason, setSeedSeason] = useState(String(DEFAULT_SEASON));
  const [cleanupSeason, setCleanupSeason] = useState(String(DEFAULT_SEASON));
  const [cleanupType, setCleanupType] = useState("reserve_flags");
  const [seedOpen, setSeedOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [resetLineupStateOpen, setResetLineupStateOpen] = useState(false);

  const { data: officialMatches = [], isLoading: matchesLoading } = useOfficialMatches({ includeUsed: true, staleTime: 30_000 });
  const { data: official, isLoading: officialLoading } = useOfficialMatch(selectedId, { enabled: !!selectedId });

  const { data: seedStatus, refetch: refetchSeedStatus } = useQuery({
    queryKey: ["adminSeedStatus", seedSeason],
    queryFn: () => getSeedStatus(seedSeason),
    staleTime: 30_000,
  });

  const matches = useMemo(() => {
    const season = Number(matchSeason);
    const filtered = (Array.isArray(officialMatches) ? officialMatches : []).filter((match) => {
      if (matchSeasonValue(match) !== season) return false;
      const ready = hasLineup(match);
      if (statusFilter === "missing" && ready) return false;
      if (statusFilter === "ready" && !ready) return false;
      if (statusFilter === "upcoming" && !isUpcoming(match)) return false;
      if (statusFilter === "upcoming_missing" && (!isUpcoming(match) || ready)) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const aTime = matchDate(a)?.getTime() ?? 0;
      const bTime = matchDate(b)?.getTime() ?? 0;
      const aBucket = matchSortBucket(a);
      const bBucket = matchSortBucket(b);
      if (aBucket !== bBucket) return aBucket - bBucket;
      if (aBucket !== 2 && hasLineup(a) !== hasLineup(b)) return hasLineup(a) ? 1 : -1;
      return aBucket === 2 ? bTime - aTime : aTime - bTime;
    });
  }, [officialMatches, matchSeason, statusFilter]);

  useEffect(() => {
    if (!matches.length) {
      setSelectedId("");
      return;
    }
    if (!matches.some((match) => String(match?.id ?? match?._id ?? "") === String(selectedId))) {
      setSelectedId(String(matches[0]?.id ?? matches[0]?._id ?? ""));
    }
  }, [matches, selectedId]);

  useEffect(() => {
    let cancelled = false;
    async function loadRiders() {
      if (!official?.home_team_id || !official?.away_team_id) {
        setHomeOptions([]);
        setAwayOptions([]);
        setHomeState(emptyState());
        setAwayState(emptyState());
        return;
      }
      try {
        const [homePayload, awayPayload] = await Promise.all([
          getTeamRiders(official.home_team_id, {
            season: Number(riderSeason),
            active: true,
            includeGuests: true,
            officialMatchId: official.id,
          }),
          getTeamRiders(official.away_team_id, {
            season: Number(riderSeason),
            active: true,
            includeGuests: true,
            officialMatchId: official.id,
          }),
        ]);
        if (cancelled) return;
        setHomeOptions(toOptions(homePayload));
        setAwayOptions(toOptions(awayPayload));
        setHomeState(buildLineupState(official?.lineups?.home));
        setAwayState(buildLineupState(official?.lineups?.away));
      } catch (error) {
        if (!cancelled) toast.error(error?.message || "Kunde inte hämta lagens förare.");
      }
    }
    loadRiders();
    return () => {
      cancelled = true;
    };
  }, [official, riderSeason]);

  const overview = useMemo(() => {
    const seasonMatches = (Array.isArray(officialMatches) ? officialMatches : []).filter((match) => matchSeasonValue(match) === Number(matchSeason));
    const ready = seasonMatches.filter(hasLineup).length;
    const upcoming = seasonMatches.filter(isUpcoming).length;
    return { total: seasonMatches.length, ready, upcoming, missing: seasonMatches.length - ready };
  }, [officialMatches, matchSeason]);

  const isDirty = useMemo(() => {
    return !sameSelection(homeState, buildLineupState(official?.lineups?.home)) || !sameSelection(awayState, buildLineupState(official?.lineups?.away));
  }, [official, homeState, awayState]);

  const saveMutation = useMutation({
    mutationFn: () => updateOfficialMatchLineup(selectedId, { home: lineupToPayload(homeState, homeOptions), away: lineupToPayload(awayState, awayOptions) }),
    onSuccess: async () => {
      toast.success("Laguppställningen sparades.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["officialMatches"] }),
        queryClient.invalidateQueries({ queryKey: ["officialMatch", selectedId] }),
      ]);
    },
    onError: (error) => toast.error(error?.message || "Kunde inte spara laguppställningen."),
  });

  const clearMutation = useMutation({
    mutationFn: () => clearOfficialMatchLineup(selectedId),
    onSuccess: async () => {
      toast.success("Laguppställningen rensades.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["officialMatches"] }),
        queryClient.invalidateQueries({ queryKey: ["officialMatch", selectedId] }),
      ]);
    },
    onError: (error) => toast.error(error?.message || "Kunde inte rensa laguppställningen."),
  });

  const resetLineupStateMutation = useMutation({
    mutationFn: () => resetOfficialMatchLineupState(selectedId),
    onSuccess: async () => {
      setResetLineupStateOpen(false);
      toast.success("Lineup-status återställdes helt.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["officialMatches"] }),
        queryClient.invalidateQueries({ queryKey: ["officialMatch", selectedId] }),
      ]);
    },
    onError: (error) => toast.error(error?.message || "Kunde inte återställa lineup-status."),
  });

  const seedMutation = useMutation({
    mutationFn: () => runSeasonSeed(seedSeason),
    onSuccess: async (result) => {
      setSeedOpen(false);
      toast.success(`${Number(result?.inserted ?? result?.added ?? 0)} förare lades till för ${seedSeason}.`);
      await refetchSeedStatus();
    },
    onError: (error) => toast.error(error?.message || "Seedning misslyckades."),
  });

  const cleanupMutation = useMutation({
    mutationFn: () => {
      if (cleanupType === "reserve_flags") return cleanupRiderReserveFlags({ season: cleanupSeason });
      if (cleanupType === "lineup_nos") return cleanupRiderLineupNos({ season: cleanupSeason });
      return cleanupRiderNumbers({ season: cleanupSeason });
    },
    onSuccess: (result) => {
      setCleanupOpen(false);
      toast.success(`${Number(result?.updated ?? result?.count ?? 0)} poster uppdaterades.`);
    },
    onError: (error) => toast.error(error?.message || "Datastädningen misslyckades."),
  });

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <main className="container mx-auto space-y-6 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold">Laguppställningar</h1>
          <p className="text-sm text-muted-foreground">Översikt och hantering av laguppställningar för officiella matcher.</p>
        </div>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={CalendarDays} label="Säsong" value={matchSeason} hint={`${overview.total} matcher`} />
          <StatCard icon={Clock3} label="Kommande" value={overview.upcoming} hint="Matcher framför dig" />
          <StatCard icon={CheckCircle2} label="Klara" value={overview.ready} hint="Har uppställning" />
          <StatCard icon={AlertTriangle} label="Saknas" value={overview.missing} hint="Behöver åtgärd" />
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.1fr,1.9fr]">
          <Card>
            <CardHeader>
              <CardTitle>Matchöversikt</CardTitle>
              <CardDescription>Kommande matcher utan lineup prioriteras högst.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <div className="text-amber-900">
                  <span className="font-semibold">OBS!</span> Använd detta främst innan matchstart. Om heat redan har körts
                  bör användaren i stället justera laguppställningen manuellt i sitt eget protokoll.
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabeledSelect label="Matchsäsong" value={matchSeason} onChange={setMatchSeason} options={seasons()} />
                <LabeledSelect
                  label="Filter"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: "upcoming_missing", label: "Kommande som saknar" },
                    { value: "upcoming", label: "Alla kommande" },
                    { value: "missing", label: "Alla som saknar" },
                    { value: "ready", label: "Har uppställning" },
                    { value: "all", label: "Alla matcher" },
                  ]}
                />
              </div>

              <div className="space-y-3">
                {matches.length ? matches.map((match) => {
                  const id = String(match?.id ?? match?._id ?? "");
                  const selected = id === selectedId;
                  const badge = hasLineup(match) ? { label: "Klar", variant: "default" } : { label: "Saknas", variant: "destructive" };
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSelectedId(id)}
                      className={`w-full rounded-xl border p-4 text-left transition ${selected ? "border-primary bg-primary/5" : "hover:border-primary/40 hover:bg-muted/40"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">{matchTitle(match)}</div>
                          <div className="text-sm text-muted-foreground">{matchMeta(match) || "Saknar datum"}</div>
                        </div>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </div>
                    </button>
                  );
                }) : (
                  <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">Inga matcher matchar filtret.</div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Välj specifik match</label>
                <Select value={selectedId} onValueChange={setSelectedId} disabled={!matches.length}>
                  <SelectTrigger><SelectValue placeholder="Välj match" /></SelectTrigger>
                  <SelectContent className="bg-background">
                    {matches.map((match) => {
                      const id = String(match?.id ?? match?._id ?? "");
                      const meta = formatSvDateTime(match?.date, {
                        dateStyle: "short",
                        timeStyle: "short",
                      });
                      return (
                        <SelectItem key={id} value={id}>
                          {meta ? `${meta} • ${matchTitle(match)}` : matchTitle(match)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Matchens laguppställning</CardTitle>
              <CardDescription>Plats 6 och 7 markeras som reserver för matchen, inte permanent på föraren.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <LabeledSelect label="Förarsäsong" value={riderSeason} onChange={setRiderSeason} options={seasons()} />
                <div className="rounded-xl border bg-muted/30 px-4 py-3 text-sm">
                  <div className="font-medium">{official ? matchTitle(official) : "Ingen match vald"}</div>
                  <div className="text-muted-foreground">{official ? matchMeta(official) || "Saknar datum" : "Välj en match i listan"}</div>
                </div>
              </div>

              {matchesLoading || officialLoading ? (
                <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">Hämtar data...</div>
              ) : !official ? (
                <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">Välj en match för att redigera uppställningen.</div>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  <LineupColumn title={official?.home_team_display || official?.home_team_name_canonical || official?.home_team || "Hemmalag"} state={homeState} setState={setHomeState} options={homeOptions} />
                  <LineupColumn title={official?.away_team_display || official?.away_team_name_canonical || official?.away_team || "Bortalag"} state={awayState} setState={setAwayState} options={awayOptions} />
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/20 p-4">
                <div className="text-sm text-muted-foreground">{isDirty ? "Det finns osparade ändringar." : "Ingen osparad ändring för vald match."}</div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setResetLineupStateOpen(true)}
                    disabled={!selectedId || resetLineupStateMutation.isPending || clearMutation.isPending || saveMutation.isPending}
                  >
                    Återställ lineup-status
                  </Button>
                  <Button variant="outline" onClick={() => clearMutation.mutate()} disabled={!selectedId || clearMutation.isPending || saveMutation.isPending}>
                    <Trash2 className="h-4 w-4" />
                    Rensa
                  </Button>
                  <Button onClick={() => saveMutation.mutate()} disabled={!selectedId || saveMutation.isPending || clearMutation.isPending}>
                    <Save className="h-4 w-4" />
                    {saveMutation.isPending ? "Sparar..." : "Spara laguppställning"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="grid gap-6 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />Seed från roster</CardTitle>
              <CardDescription>Lägger till saknade förare för vald säsong.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <LabeledSelect label="Säsong att seeda" value={seedSeason} onChange={setSeedSeason} options={seasons()} />
              <div className="rounded-xl border bg-muted/20 p-4 text-sm">
                <div>Roster-förare: {Number(seedStatus?.roster_count ?? 0)}</div>
                <div>Seedade förare: {Number(seedStatus?.seeded_count ?? 0)}</div>
                <div>Saknas att lägga till: {Number(seedStatus?.missing_count ?? 0)}</div>
              </div>
              <Button className="w-full" onClick={() => setSeedOpen(true)}>Kör seed</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5 text-primary" />Datastädning</CardTitle>
              <CardDescription>Rensa gammal rider-data som inte längre ska ligga permanent.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <LabeledSelect label="Säsong" value={cleanupSeason} onChange={setCleanupSeason} options={seasons()} />
              <LabeledSelect
                label="Typ av städning"
                value={cleanupType}
                onChange={setCleanupType}
                options={[
                  { value: "reserve_flags", label: "Rensa reservflaggor" },
                  { value: "lineup_nos", label: "Rensa lineup-nummer" },
                  { value: "numbers", label: "Rensa permanenta nummer" },
                ]}
              />
              <Button variant="outline" className="w-full" onClick={() => setCleanupOpen(true)}>Kör datastädning</Button>
            </CardContent>
          </Card>

        </section>
      </main>

      <ConfirmDialog open={seedOpen} onOpenChange={setSeedOpen} title={`Kör seed för ${seedSeason}?`} description="Funktionen ska bara lägga till saknade lag och förare, inte skapa dubletter." actionLabel={seedMutation.isPending ? "Kör..." : "Bekräfta"} onAction={() => seedMutation.mutate()} disabled={seedMutation.isPending} />
      <ConfirmDialog open={cleanupOpen} onOpenChange={setCleanupOpen} title="Kör datastädning?" description={`Detta uppdaterar rider-data för säsong ${cleanupSeason}.`} actionLabel={cleanupMutation.isPending ? "Kör..." : "Bekräfta"} onAction={() => cleanupMutation.mutate()} disabled={cleanupMutation.isPending} />
      <ConfirmDialog
        open={resetLineupStateOpen}
        onOpenChange={setResetLineupStateOpen}
        title="Återställ lineup-status helt?"
        description="Detta tar bort lineups, lineup-signatur, lineup-updaterad-tid och sätter lineup-version till 0 för vald match."
        actionLabel={resetLineupStateMutation.isPending ? "Återställer..." : "Bekräfta"}
        onAction={() => resetLineupStateMutation.mutate()}
        disabled={resetLineupStateMutation.isPending}
      />

    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="flex items-center gap-2 text-2xl"><Icon className="h-5 w-5 text-primary" />{value}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-sm text-muted-foreground">{hint}</CardContent>
    </Card>
  );
}

function LabeledSelect({ label, value, onChange, options }) {
  const normalized = options.map((option) => typeof option === "string" ? { value: option, label: option } : option);
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent className="bg-background">
          {normalized.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function LineupColumn({ title, state, setState, options }) {
  return (
    <Card className="shadow-none">
      <CardHeader className="pb-4"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {LINEUP_SLOTS.map((slot) => (
          <div key={`${title}-${slot}`} className="space-y-2">
            <label className="text-sm font-medium">{slotLabel(slot)}</label>
            <Select value={state[String(slot)] || ""} onValueChange={(value) => setState((prev) => ({ ...prev, [String(slot)]: value }))}>
              <SelectTrigger><SelectValue placeholder="Välj förare" /></SelectTrigger>
              <SelectContent className="bg-background">
                {options.map((option) => {
                  const selectedInOtherSlot = LINEUP_SLOTS.some(
                    (otherSlot) =>
                      otherSlot !== slot &&
                      String(state[String(otherSlot)] || "") === String(option.value)
                  );
                  return (
                    <SelectItem
                      key={`${slot}-${option.value}`}
                      value={option.value}
                      disabled={selectedInOtherSlot}
                    >
                      {option.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ConfirmDialog({ open, onOpenChange, title, description, actionLabel, onAction, disabled }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={disabled}>Avbryt</AlertDialogCancel>
          <AlertDialogAction onClick={onAction} disabled={disabled}>{actionLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
